import {
  ReportItem,
  ReportItemConstructor,
  ToCommonURLOptions,
} from './ReportItem'

export class SharePointPacketReportItem<
  ID extends Lowercase<string>,
  Name extends string,
  ClassType extends string,
  ClassTypeName extends string,
  Endpoint extends string,
  QueryParameter extends 'construction-admin-submittal' | 'packet',
  Label extends string,
  SampleFilename extends string,
  Key extends 'id' | 'globalId',
  RecordId = QueryParameter extends 'packet'
    ? {
        /**
         * the globalId of the record associated with the report
         */
        globalId: string
      }
    : {
        /**
         * the id of the record associated with the report
         */
        recordId: string
      }
> extends ReportItem<
  ID,
  Name,
  ClassType,
  ClassTypeName,
  Endpoint,
  QueryParameter,
  Label,
  SampleFilename,
  SharePointPacketReportItemUrlOptions<RecordId>,
  Key
> {
  /**
   * @see {@link SharePointPacketReportItemUrlOptions}
   * @see {@link RecordId}
   */
  toURL({
    baseURL,
    projectId,
    driveItemIds,
    skipSharePointUpload,
    ...options
  }: SharePointPacketReportItemUrlOptions<RecordId>): string {
    const url = this.__toURLObject({ baseURL, projectId })
    if (driveItemIds) url.searchParams.set('files', driveItemIds.join(','))
    url.searchParams.set(
      'skipSharePointUpload',
      `${skipSharePointUpload ?? false}`
    )
    const { globalId, recordId } = options as {
      recordId?: string
      globalId?: string
    }
    if (globalId) this.__addGlobalId(url, globalId)
    else if (recordId) url.searchParams.set('recordId', recordId)
    else
      throw new Error(
        `Cannot create a URL for ${this.name} without a ${
          this.queryParameter === 'packet' ? 'globalId' : 'recordId'
        }`
      )
    return url.toString()
  }
  constructor(
    options: ReportItemConstructor<
      ID,
      Name,
      ClassType,
      ClassTypeName,
      Endpoint,
      QueryParameter,
      Label,
      SampleFilename,
      Key,
      SharePointPacketReportItemUrlOptions<RecordId>
    >
  ) {
    super(options)
  }
}

type SharePointPacketReportItemUrlOptions<T> = ToCommonURLOptions &
  T & {
    /**
     * a list of DriveItem ids to attach to the report
     *
     * these should come from SharePoint through Microsoft's Graph API
     */
    driveItemIds?: string[]
    /**
     * @default false
     */
    skipSharePointUpload?: true
  }
